import React, { useState, useMemo, useEffect } from 'react';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Clock, 
  Target, 
  RefreshCw, 
  BarChart3, 
  PieChart, 
  AlertCircle, 
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Trophy,
  Activity,
  MessageSquare,
  Phone,
  Filter,
  Calendar,
  CheckCircle,
  XCircle,
  Minus
} from 'lucide-react';
import apiClient from '../lib/apiClient';

const PartnerAdminDashboard = ({ 
  data, 
  loading, 
  apiConnected, 
  onRefreshData, 
  error, 
  user 
}) => {
    const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
    const [selectedView, setSelectedView] = useState('overview');
    const [selectedSource, setSelectedSource] = useState('all');
    const [selectedTenant, setSelectedTenant] = useState('all');
    const [availableTenants, setAvailableTenants] = useState([]);
  // Calculate derived metrics
  const derivedMetrics = useMemo(() => {
    if (!data.corePerformance) return null;

    const {
      totalLeadsAssigned = 0,
      totalHandoffs = 0,
      totalPipelineValue = 0,
      totalConversions = 0,
      avgTimeToHandoff = 0
    } = data.corePerformance || {};

    return {
      leadToHandoffRate: totalLeadsAssigned > 0 ? (totalHandoffs / totalLeadsAssigned * 100).toFixed(2) : '0.00',
      handoffToPipelineRate: totalHandoffs > 0 ? (totalConversions / totalHandoffs * 100).toFixed(2) : '0.00',
      avgPipelineValuePerHandoff: totalHandoffs > 0 ? (totalPipelineValue / totalHandoffs).toFixed(0) : '0',
      conversionEfficiency: totalLeadsAssigned > 0 ? (totalConversions / totalLeadsAssigned * 100).toFixed(2) : '0.00'
    };
  }, [data.corePerformance]);

          useEffect(() => {
  if (user?.role === 'partner_admin') {
    fetchAvailableTenants();
  }
}, [user]);

  // Format utilities
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatTime = (minutes) => {
    if (!minutes) return '0m';
    if (minutes >= 60) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  const fetchAvailableTenants = async () => {
  try {
    const response = await apiClient.get('/enterprise-analytics/tenants', {
      headers: {
        'X-User-Role': 'partner_admin'
      }
    });
    setAvailableTenants(response.data.tenants || []);
  } catch (error) {
    console.error('Failed to fetch tenants:', error);
  }
};

  // Trend indicator component
  const TrendIndicator = ({ value, isPositive = true, suffix = '%' }) => {
    if (!value) return <span className="text-gray-400">-</span>;
    
    const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
    const colorClass = isPositive ? 'text-green-600' : 'text-red-600';
    
    return (
      <div className={`flex items-center ${colorClass}`}>
        <Icon className="h-4 w-4 mr-1" />
        <span className="text-sm font-medium">{value}{suffix}</span>
      </div>
    );
  };

  // Core Performance Metrics Section
  const CorePerformanceSection = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Core Performance Metrics</h2>
        <div className="flex items-center space-x-2">
          <select 
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1 text-sm"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Lead-to-Handoff Rate"
          value={`${derivedMetrics?.leadToHandoffRate || '0'}%`}
          trend={data.corePerformance?.handoffRateTrend}
          icon={<Target className="h-6 w-6 text-blue-600" />}
          description="AI qualification effectiveness"
          highlighted
        />
        
        <MetricCard
          title="Handoff-to-Pipeline Rate"
          value={`${derivedMetrics?.handoffToPipelineRate || '0'}%`}
          trend={data.corePerformance?.pipelineConversionTrend}
          icon={<TrendingUp className="h-6 w-6 text-green-600" />}
          description="Your ROI metric"
          highlighted
        />
        
        <MetricCard
          title="Total Pipeline Value"
          value={formatCurrency(data.corePerformance?.totalPipelineValue)}
          trend={data.corePerformance?.pipelineValueTrend}
          icon={<DollarSign className="h-6 w-6 text-emerald-600" />}
          description="Financial score"
        />
        
        <MetricCard
          title="Avg Pipeline Value"
          value={formatCurrency(derivedMetrics?.avgPipelineValuePerHandoff)}
          trend={data.corePerformance?.avgValueTrend}
          icon={<BarChart3 className="h-6 w-6 text-purple-600" />}
          description="Per handoff value"
        />
      </div>

      {/* User Performance Leaderboard */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">User Performance Leaderboard</h3>
        {data.userPerformanceLeaderboard && data.userPerformanceLeaderboard.length > 0 ? (
          <div className="space-y-3">
            {data.userPerformanceLeaderboard.map((user, idx) => (
              <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${
                idx === 0 ? 'bg-yellow-50 border border-yellow-200' :
                idx === 1 ? 'bg-gray-50 border border-gray-200' :
                idx === 2 ? 'bg-orange-50 border border-orange-200' :
                'bg-gray-50'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    idx === 0 ? 'bg-yellow-100 text-yellow-800' :
                    idx === 1 ? 'bg-gray-100 text-gray-800' :
                    idx === 2 ? 'bg-orange-100 text-orange-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {idx < 3 ? <Trophy className="h-4 w-4" /> : <span className="text-sm font-medium">{idx + 1}</span>}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user.name}</p>
                    <p className="text-sm text-gray-500">{user.role || 'Sales Rep'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{user.handoffToPipelineRate}% conversion</p>
                  <p className="text-sm text-gray-500">{formatCurrency(user.pipelineValue)} pipeline</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>User performance data loading...</p>
          </div>
        )}
      </div>
    </div>
  );

  // Efficiency & Velocity Metrics Section
  const EfficiencyMetricsSection = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Efficiency & Velocity Metrics</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Time-to-Handoff"
          value={formatTime(data.efficiencyMetrics?.avgTimeToHandoff)}
          trend={data.efficiencyMetrics?.handoffTimeTrend}
          icon={<Clock className="h-6 w-6 text-blue-600" />}
          description="AI qualification speed"
        />
        
        <MetricCard
          title="Human Response Time"
          value={formatTime(data.efficiencyMetrics?.avgResponseTime)}
          trend={data.efficiencyMetrics?.responseTimeTrend}
          icon={<Zap className="h-6 w-6 text-orange-600" />}
          description="Rep follow-up speed"
        />
        
        <MetricCard
          title="Hot Lead Decay Rate"
          value={`${data.efficiencyMetrics?.hotLeadDecayRate || '0'}%`}
          trend={data.efficiencyMetrics?.decayRateTrend}
          icon={<AlertCircle className="h-6 w-6 text-red-600" />}
          description="Hourly conversion drop"
        />
        
        <MetricCard
          title="Follow-up Effectiveness"
          value={`${data.efficiencyMetrics?.followUpEffectiveness || '0'}%`}
          trend={data.efficiencyMetrics?.followUpTrend}
          icon={<CheckCircle className="h-6 w-6 text-green-600" />}
          description="Fast follow-up success rate"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Response Time Distribution</h3>
          {data.timeBasedAnalytics?.responseTimeDistribution ? (
            <div className="space-y-3">
              {Object.entries(data.timeBasedAnalytics.responseTimeDistribution).map(([timeRange, percentage]) => (
                <div key={timeRange} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{timeRange}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium w-8">{percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              <Clock className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Response time data loading...</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Peak Performance Hours</h3>
          {data.timeBasedAnalytics?.peakHours ? (
            <div className="space-y-3">
              {data.timeBasedAnalytics.peakHours.map((hour, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{hour.time}</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">{hour.conversionRate}% conversion</span>
                    <span className="text-xs text-gray-500">({hour.volume} leads)</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              <Activity className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Peak hours analysis loading...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Lead Quality & Rule Optimization Section
  const LeadQualitySection = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Lead Quality & Rule Optimization</h2>
        <select 
          value={selectedSource}
          onChange={(e) => setSelectedSource(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1 text-sm"
        >
          <option value="all">All Sources</option>
          <option value="website">Website Forms</option>
          <option value="social">Social Media</option>
          <option value="referral">Referrals</option>
          <option value="ads">Paid Ads</option>
        </select>
      </div>

      {/* Source Performance */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Source Performance Analysis</h3>
        {data.sourcePerformance && data.sourcePerformance.length > 0 ? (
          <div className="space-y-4">
            {data.sourcePerformance.map((source, idx) => (
              <div key={idx} className="border border-gray-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">{source.name}</h4>
                  <span className="text-sm text-gray-500">{formatNumber(source.totalLeads)} leads</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Handoff Rate</span>
                    <p className="font-medium">{source.handoffRate}%</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Pipeline Rate</span>
                    <p className="font-medium">{source.pipelineRate}%</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Avg Value</span>
                    <p className="font-medium">{formatCurrency(source.avgPipelineValue)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <PieChart className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>Source performance data loading...</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Qualification Rules */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Qualification Rules</h3>
          {data.qualificationRules?.topRules ? (
            <div className="space-y-3">
              {data.qualificationRules.topRules.map((rule, idx) => (
                <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900">{rule.description}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-gray-600">Impact</span>
                    <span className="text-sm font-medium text-green-700">+{rule.impactPercentage}% handoff rate</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              <Filter className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Qualification rules loading...</p>
            </div>
          )}
        </div>

        {/* AI vs Human Intervention */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">AI vs Human Intervention</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">AI-Only Qualifications</span>
              <div className="flex items-center space-x-2">
                <span className="font-medium">{data.leadQualityMetrics?.aiOnlyRate || '0'}%</span>
                <CheckCircle className="h-4 w-4 text-green-600" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Manual Overrides</span>
              <div className="flex items-center space-x-2">
                <span className="font-medium">{data.leadQualityMetrics?.manualOverrideRate || '0'}%</span>
                <Users className="h-4 w-4 text-blue-600" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">AI Accuracy Score</span>
              <div className="flex items-center space-x-2">
                <span className="font-medium">{data.leadQualityMetrics?.aiAccuracyScore || '0'}%</span>
                <Target className="h-4 w-4 text-purple-600" />
              </div>
            </div>
            
            {/* Rule Effectiveness Insights */}
            {data.qualificationRules?.ruleEffectiveness && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Rule Adjustments Needed</h4>
                {data.qualificationRules.ruleEffectiveness.map((insight, idx) => (
                  <div key={idx} className="text-xs text-gray-600 mb-1">
                    • {insight.suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Engagement & Platform Usage Section
  const EngagementSection = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Engagement & Platform Usage</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Daily Active Users"
          value={formatNumber(data.engagementMetrics?.dailyActiveUsers)}
          trend={data.engagementMetrics?.dauTrend}
          icon={<Users className="h-6 w-6 text-blue-600" />}
          description="Unique daily logins"
        />
        
        <MetricCard
          title="Feature Adoption Rate"
          value={`${data.engagementMetrics?.featureAdoptionRate || '0'}%`}
          trend={data.engagementMetrics?.adoptionTrend}
          icon={<Activity className="h-6 w-6 text-green-600" />}
          description="Key features used"
        />
        
        <MetricCard
          title="Avg Session Duration"
          value={formatTime(data.engagementMetrics?.avgSessionDuration)}
          trend={data.engagementMetrics?.sessionTrend}
          icon={<Clock className="h-6 w-6 text-purple-600" />}
          description="Time spent in platform"
        />
        
        <MetricCard
          title="Handoff Button Usage"
          value={`${data.engagementMetrics?.handoffButtonUsage || '0'}%`}
          trend={data.engagementMetrics?.handoffUsageTrend}
          icon={<MessageSquare className="h-6 w-6 text-orange-600" />}
          description="Manual handoff frequency"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Feature Usage Breakdown</h3>
          {data.engagementMetrics?.featureUsage ? (
            <div className="space-y-3">
              {Object.entries(data.engagementMetrics.featureUsage).map(([feature, usage]) => (
                <div key={feature} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 capitalize">{feature.replace('_', ' ')}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${usage.percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium w-8">{usage.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              <Activity className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Feature usage data loading...</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Activity Pattern</h3>
          {data.engagementMetrics?.weeklyPattern ? (
            <div className="space-y-3">
              {data.engagementMetrics.weeklyPattern.map((day, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{day.dayName}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${day.activityLevel}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{day.activeUsers}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Activity pattern loading...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Reusable Metric Card Component
  const MetricCard = ({ title, value, trend, icon, description, highlighted = false }) => (
    <div className={`bg-white rounded-lg border p-6 ${highlighted ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className={`text-2xl font-bold ${highlighted ? 'text-blue-900' : 'text-gray-900'}`}>
            {value}
          </p>
          {description && (
            <p className="text-xs text-gray-500 mt-1">{description}</p>
          )}
        </div>
        <div className="ml-4">
          {icon}
        </div>
      </div>
      {trend && (
        <div className="mt-3">
          <TrendIndicator 
            value={Math.abs(trend)} 
            isPositive={trend > 0}
          />
        </div>
      )}
    </div>
  );

  // Handle loading state
  if (loading && (!data.corePerformance && !data.userPerformanceLeaderboard)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Partner Analytics</h2>
          <p className="text-gray-600">Analyzing your network performance and team metrics...</p>
        </div>
      </div>
    );
  }
  // Main Dashboard Layout
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Partner Dashboard</h1>
              <p className="text-gray-600 mt-1">
                Your network performance insights and team analytics
              </p>
            </div>
<div className="flex items-center space-x-4">
  <select 
    value={selectedTenant}
    onChange={(e) => setSelectedTenant(e.target.value)}
    className="border border-gray-300 rounded-md px-3 py-1 text-sm"
  >
    <option value="all">All Locations</option>
    {availableTenants.map(tenant => (
      <option key={tenant.id} value={tenant.id}>
        {tenant.name}
      </option>
    ))}
  </select>
  <div className="flex items-center space-x-2">
    <div className={`w-3 h-3 rounded-full ${apiConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
    <span className="text-sm text-gray-600">
      {apiConnected ? 'Connected' : 'Disconnected'}
    </span>
  </div>
  <button
                onClick={onRefreshData}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh Data
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error Loading Data</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-12">
          <CorePerformanceSection />
          <EfficiencyMetricsSection />
          <LeadQualitySection />
          <EngagementSection />
        </div>
      </div>
    </div>
  );
};

export default PartnerAdminDashboard;